import { useRef, useState } from "react";
import { FolderOpen, UploadCloud, Eye, Trash2, Loader2, FileText } from "lucide-react";
import type { Insured } from "@shared/schema";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FLAG_GENERATE_ACORD_FORM } from "@shared/acord-forms";
import { useFeatureFlag, useInsuredDocuments, type InsuredDocumentRow } from "@/lib/api";
import { GenerateAcordFormDialog, type AcordFormHandoff } from "./GenerateAcordFormDialog";
import { AcordFormEditorDialog } from "./AcordFormEditorDialog";
import { UploadDocumentDialog } from "./UploadDocumentDialog";
import { DocumentViewerDialog } from "./DocumentViewerDialog";
import { DeleteDocumentDialog } from "./DeleteDocumentDialog";
import {
  formatDocumentPolicyNumbers,
  formatHumanFileSize,
  formatTableTimestamp,
} from "./document-helpers";

interface Props {
  insured: Insured;
}

export function DocumentsTab({ insured }: Props) {
  // CSR-2062 — the whole ACORD surface is gated until the editor (CSR-2063) and
  // the per-form pre-fill maps (CSR-2058..CSR-2061) land. OFF (the default, and
  // the answer while the flag list is loading) leaves this tab exactly as it is
  // today: no button, no modal mounted.
  const acordFormsEnabled = useFeatureFlag(FLAG_GENERATE_ACORD_FORM);
  const { data: documents, isLoading } = useInsuredDocuments(insured.id);
  const rows = documents ?? [];

  const [uploadOpen, setUploadOpen] = useState(false);
  const [generateAcordOpen, setGenerateAcordOpen] = useState(false);
  // CSR-2063 — the generated form being edited. Holding the handoff rather than an
  // `open` flag is what makes the editor stateless between generations: closing it
  // unmounts it, so the next form never opens on the previous one's edits.
  const [acordHandoff, setAcordHandoff] = useState<AcordFormHandoff | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [viewerDoc, setViewerDoc] = useState<InsuredDocumentRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InsuredDocumentRow | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openUploadFromButton = () => {
    setPendingFile(null);
    setUploadOpen(true);
  };

  const openUploadWithFile = (file: File) => {
    setPendingFile(file);
    setUploadOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="documents-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <FolderOpen className="w-6 h-6 text-foreground" aria-hidden="true" />
          <div>
            <h2 className="text-lg font-semibold leading-tight">Documents</h2>
            <p className="text-sm text-muted-foreground">
              Upload and organize files for {insured.name ?? "this insured"}.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {acordFormsEnabled && (
            <Button
              variant="outline"
              onClick={() => setGenerateAcordOpen(true)}
              data-testid="button-generate-acord-form"
            >
              <FileText className="w-4 h-4 mr-2" aria-hidden="true" />
              Generate ACORD Form
            </Button>
          )}
          <Button onClick={openUploadFromButton} data-testid="button-upload-document">
            <UploadCloud className="w-4 h-4 mr-2" aria-hidden="true" />
            Upload Document
          </Button>
        </div>
      </div>

      {/* Persistent drop zone — visible regardless of whether docs exist. */}
      <label
        className="flex flex-col items-center gap-2 border-2 border-dashed rounded-lg px-4 py-10 cursor-pointer hover:bg-muted/50 transition-colors text-center"
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.classList.add("border-[#F1B72C]", "bg-[#F1B72C]/5");
        }}
        onDragLeave={(e) => {
          e.currentTarget.classList.remove("border-[#F1B72C]", "bg-[#F1B72C]/5");
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("border-[#F1B72C]", "bg-[#F1B72C]/5");
          const file = e.dataTransfer.files?.[0];
          if (file) openUploadWithFile(file);
        }}
        data-testid="dropzone-document"
      >
        <UploadCloud className="w-8 h-8 text-muted-foreground/60" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium">Upload documents</p>
          <p className="text-xs text-muted-foreground mt-1">
            Drop a file here or click to browse — PDF, image, or any other file.
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) openUploadWithFile(file);
            // reset value so re-selecting the same file fires onChange.
            e.currentTarget.value = "";
          }}
          data-testid="input-document-file"
        />
      </label>

      {/* List */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold">Uploaded</h3>
          <span
            className="inline-flex items-center justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
            data-testid="documents-count-badge"
          >
            {rows.length}
          </span>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Loading documents...
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6" data-testid="documents-empty-state">
            No documents uploaded yet.
          </p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NAME</TableHead>
                  <TableHead>POLICY #</TableHead>
                  <TableHead>FILE</TableHead>
                  <TableHead>SIZE</TableHead>
                  <TableHead>UPLOADED</TableHead>
                  <TableHead>UPLOADED BY</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((doc) => (
                  <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                    <TableCell className="font-medium">{doc.name}</TableCell>
                    <TableCell
                      className="text-muted-foreground"
                      data-testid={`document-policy-${doc.id}`}
                    >
                      {formatDocumentPolicyNumbers(doc.policyNumbers)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{doc.fileName}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatHumanFileSize(doc.fileSize ?? 0)}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatTableTimestamp(doc.createdAt as unknown as string)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {doc.uploadedByName ?? "Unknown"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setViewerDoc(doc)}
                        aria-label={`View ${doc.name}`}
                        data-testid={`button-view-${doc.id}`}
                      >
                        <Eye className="w-4 h-4" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(doc)}
                        aria-label={`Delete ${doc.name}`}
                        data-testid={`button-delete-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" aria-hidden="true" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {acordFormsEnabled && (
        <GenerateAcordFormDialog
          open={generateAcordOpen}
          onOpenChange={setGenerateAcordOpen}
          insuredId={insured.id}
          onGenerated={setAcordHandoff}
        />
      )}
      {acordFormsEnabled && acordHandoff !== null && (
        <AcordFormEditorDialog
          handoff={acordHandoff}
          insuredId={insured.id}
          onClose={() => setAcordHandoff(null)}
        />
      )}
      <UploadDocumentDialog
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) setPendingFile(null);
        }}
        insuredId={insured.id}
        prefillFile={pendingFile}
      />
      <DocumentViewerDialog
        open={viewerDoc !== null}
        onOpenChange={(open) => {
          if (!open) setViewerDoc(null);
        }}
        document={viewerDoc}
      />
      <DeleteDocumentDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        document={deleteTarget}
        insuredId={insured.id}
      />
    </div>
  );
}
